import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  Stack,
  Typography
} from '@mui/material'
import React, { type ChangeEvent, useMemo, useState } from 'react'
import { useQueryClient } from 'react-query'
import { useBoolean } from 'react-use'

import { SearchField } from 'components/common/SearchField'
import {
  filterNamespaceRequests,
  filterNamespaces,
  getApprovedNamespaceRequestsFromTeam,
  getPendingNamespaceRequestsFromTeam,
  getSubmitButtonText,
  NAMESPACE_CHIP_LIST_MAX_HEIGHT
} from 'components/configurations/clusters/clusters-utils'
import { PendingNamespaceAccessRequestAlert } from 'components/configurations/common/PendingNamespaceAccessRequestAlert'
import { PendingNamespaceAccessRequestChip } from 'components/configurations/common/PendingNamespaceAccessRequestChip'
import { SharedNamespaceChip } from 'components/configurations/common/SharedNamespaceChip'
import { UnsharedNamespaceChip } from 'components/configurations/common/UnsharedNamespaceChip'
import Icon from 'components/Icon'
import { createRequests } from 'components/kubernetes/access-control/utils'
import useAuth from 'hooks/use-auth'
import toast from 'lib/toast'
import { muiTheme } from 'mui-theme'
import { sharedAssetsQueryKeys } from 'queries/sharedAssets/query-keys'
import { usePostSharedAssetsRequests } from 'queries/sharedAssets/use-post-shared-assets-requests'
import { usePostSharedAssetsRequestsWithAction } from 'queries/sharedAssets/use-post-shared-assets-requests-with-action'
import {
  SharedAssetRequestStatus,
  SharedAssetsRequestAction
} from 'types/api/sharedAssets/requests'
import { messageFromResponseError } from 'utils/strings'

import type {
  IncomingRequestByAssetId,
  TeamWithSharedAssetsInformation
} from 'hooks/use-manage-shared-assets'
import type { OwnedK8sClusterAsset } from 'types/api/sharedAssets/sharedAssets'

export const ManageClusterAccessDrawerContent: React.FC<{
  activeTeam: TeamWithSharedAssetsInformation
  cluster: OwnedK8sClusterAsset
  handleCloseDrawer(): void
}> = ({ activeTeam, cluster, handleCloseDrawer }) => {
  const { currentTeamId: teamId } = useAuth()
  const queryClient = useQueryClient()

  const [searchValue, setSearchValue] = useState('')
  const [isSubmitting, setIsSubmitting] = useBoolean(false)
  const [isSelectAllUnsharedNamespaces, setIsSelectAllUnsharedNamespaces] =
    useBoolean(false)
  const [selectedNamespacesToAdd, setSelectedNamespacesToAdd] = useState<
    string[]
  >([])
  const [
    pendingNamespaceRequestsToRemove,
    setPendingNamespaceRequestsToRemove
  ] = useState<IncomingRequestByAssetId[]>([])
  const [
    approvedNamespaceRequestsToRemove,
    setApprovedNamespaceRequestsToRemove
  ] = useState<IncomingRequestByAssetId[]>([])

  const { mutateAsync: mutatePostSharedAssetsRequestsApprove } =
    usePostSharedAssetsRequestsWithAction(SharedAssetsRequestAction.APPROVE)
  const { mutateAsync: mutatePostSharedAssetsRequestsDeny } =
    usePostSharedAssetsRequestsWithAction(SharedAssetsRequestAction.DENY)
  const { mutateAsync: mutatePostSharedAssetsRequests } =
    usePostSharedAssetsRequests()

  const handleSubmit = async () => {
    setIsSubmitting(true)

    // Get the list of requests to approve
    const requestIdsToApprove = getPendingNamespaceRequestsFromTeam(
      activeTeam,
      pendingNamespaceRequestsToRemove
    ).map((request) => request.id)

    // Get the list of currently approved requests to deny
    const approvedRequestIdsToDeny = approvedNamespaceRequestsToRemove.map(
      (request) => request.id
    )
    // Get the list of currently pending requests to deny
    const pendingRequestIdsToDeny = pendingNamespaceRequestsToRemove.map(
      (request) => request.id
    )

    // Combine the lists of request IDs to deny
    const requestIdsToDeny = [
      ...approvedRequestIdsToDeny,
      ...pendingRequestIdsToDeny
    ]

    // Filter out the namespaces that are already approved for the active team. We don't need to create requests for these.
    const newNamespacesToAdd = selectedNamespacesToAdd.filter((namespace) => {
      return !activeTeam.requests[SharedAssetRequestStatus.APPROVED].some(
        (request) => request.namespace === namespace
      )
    })

    const requestsToCreate = createRequests({
      owningTeamId: cluster.owningTeamId,
      requestingTeamId: activeTeam.identifier,
      assetId: cluster.assetId,
      namespaces: newNamespacesToAdd
    })

    try {
      if (requestIdsToApprove.length > 0) {
        await mutatePostSharedAssetsRequestsApprove({
          requestIds: requestIdsToApprove
        })
      }

      if (requestIdsToDeny.length > 0) {
        await mutatePostSharedAssetsRequestsDeny({
          requestIds: requestIdsToDeny
        })
      }

      if (requestsToCreate.length > 0) {
        await mutatePostSharedAssetsRequests({
          newRequests: requestsToCreate
        })
      }

      setIsSubmitting(false)
      handleCloseDrawer()

      toast({
        type: 'success',
        text: 'Successfully submitted requests'
      })

      await queryClient.refetchQueries(sharedAssetsQueryKeys.get({ teamId }))
    } catch (e) {
      setIsSubmitting(false)

      toast({
        type: 'error',
        text: messageFromResponseError(
          e,
          'An error occurred submitting this request. Please try again later.'
        )
      })
    }
  }

  // Filter out the pending namespace requests that are marked for removal
  const pendingNamespaceRequests = useMemo(
    () =>
      getPendingNamespaceRequestsFromTeam(
        activeTeam,
        pendingNamespaceRequestsToRemove
      ),
    [activeTeam, pendingNamespaceRequestsToRemove]
  )

  // Filter out the approved namespace requests that are marked for removal
  const approvedNamespaceRequests = useMemo(
    () =>
      getApprovedNamespaceRequestsFromTeam(
        activeTeam,
        approvedNamespaceRequestsToRemove
      ),
    [activeTeam, approvedNamespaceRequestsToRemove]
  )

  // Get the list of available namespaces that are not in the pending or approved requests marked for removal
  const unsharedNamespaces = useMemo(() => {
    const allNamespaces = cluster.availableNamespaces
    const pendingNamespacesToRemove = pendingNamespaceRequests.map(
      ({ namespace }) => namespace
    )
    const approvedNamespacesToRemove = approvedNamespaceRequests.map(
      ({ namespace }) => namespace
    )

    const namespacesToRemove = new Set([
      ...pendingNamespacesToRemove,
      ...approvedNamespacesToRemove
    ])

    return allNamespaces.filter(
      (namespace) => !namespacesToRemove.has(namespace)
    )
  }, [
    approvedNamespaceRequests,
    cluster.availableNamespaces,
    pendingNamespaceRequests
  ])

  /**
   * Search Filtering
   */
  const filteredPendingNamespaceRequests = useMemo(
    () => filterNamespaceRequests(pendingNamespaceRequests, searchValue),
    [pendingNamespaceRequests, searchValue]
  )

  const filteredApprovedNamespaceRequests = useMemo(
    () => filterNamespaceRequests(approvedNamespaceRequests, searchValue),
    [approvedNamespaceRequests, searchValue]
  )

  const filteredUnsharedNamespaces = useMemo(
    () => filterNamespaces(unsharedNamespaces, searchValue),
    [searchValue, unsharedNamespaces]
  )
  /**
   * End Search Filtering
   */

  // Adds a namespace request to the list of pending requests to remove
  const handleDenySharedNamespaceRequest = (
    request: IncomingRequestByAssetId
  ) => {
    setPendingNamespaceRequestsToRemove((prevState) => [...prevState, request])
  }

  // Adds a namespace request to the list of approved requests (shared namespaces) to remove
  const handleRevokeSharedNamespaceAccess = (
    request: IncomingRequestByAssetId
  ) => {
    setApprovedNamespaceRequestsToRemove((prevState) => [...prevState, request])
  }

  // Adds a namespace to the list of namespaces to create an access request for
  const handleAddSharedNamespaceRequest = (
    namespace: string,
    isSelected: boolean
  ) => {
    // Check if the namespace is already in the list of namespaces to add
    const newSelectedNamespacesToAdd = isSelected
      ? selectedNamespacesToAdd.filter(
          (selectedNamespace) => selectedNamespace !== namespace
        )
      : [...selectedNamespacesToAdd, namespace]

    if (isSelectAllUnsharedNamespaces) {
      setIsSelectAllUnsharedNamespaces(false)
    }

    setSelectedNamespacesToAdd(newSelectedNamespacesToAdd)
  }

  const handleSelectAllUnsharedNamespaces = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    setIsSelectAllUnsharedNamespaces(event.target.checked)

    if (event.target.checked) {
      setSelectedNamespacesToAdd(filteredUnsharedNamespaces)
    } else {
      setSelectedNamespacesToAdd([])
    }
  }

  // Adds all approved namespace requests to the list of requests to deny
  const handleUnshareAllNamespaces = () => {
    setApprovedNamespaceRequestsToRemove(
      activeTeam.requests[SharedAssetRequestStatus.APPROVED]
    )
  }

  const isSaveDisabled =
    activeTeam.requests[SharedAssetRequestStatus.REQUESTED].length === 0 &&
    approvedNamespaceRequestsToRemove.length === 0 &&
    selectedNamespacesToAdd.length === 0

  return (
    // TODO: useFlexGap
    <Stack
      data-testid={`manage-cluster-access-drawer-content-${cluster.assetId}`}
      sx={{ rowGap: 4 }}
    >
      <Stack sx={{ rowGap: 2 }}>
        <Typography variant="h6">
          <Icon
            css={{ marginRight: muiTheme.spacing(1) }}
            icon="solidUserGroup"
          />
          {activeTeam.name}
        </Typography>
        <Typography variant="body1">
          <Icon
            css={{ marginRight: muiTheme.spacing(1) }}
            icon="dharmachakra"
          />
          {cluster.clusterName}
        </Typography>
        {pendingNamespaceRequests.length > 0 && (
          <PendingNamespaceAccessRequestAlert title="Team is waiting for namespace sharing approval" />
        )}
      </Stack>
      <SearchField
        fullWidth
        data-testid="manage-cluster-namespace-access-drawer-search"
        helperText="Search by namespace"
        label="Search"
        value={searchValue}
        onChange={setSearchValue}
      />
      <Stack sx={{ rowGap: 3 }}>
        {filteredPendingNamespaceRequests.length > 0 && (
          <Box data-testid="pending-namespace-requests-list">
            {filteredPendingNamespaceRequests.map((pendingNamespaceRequest) => {
              const { namespace, requester } = pendingNamespaceRequest

              return (
                <PendingNamespaceAccessRequestChip
                  key={namespace}
                  isRequesting={false}
                  namespace={namespace}
                  requester={requester}
                  onDelete={() =>
                    handleDenySharedNamespaceRequest(pendingNamespaceRequest)
                  }
                />
              )
            })}
          </Box>
        )}
        {filteredUnsharedNamespaces.length > 0 && (
          <Stack sx={{ rowGap: 1 }}>
            <Grid container>
              <Grid item xs={6}>
                <Typography>Available namespaces</Typography>
                <Typography variant="caption">
                  Select namespaces to grant access
                </Typography>
              </Grid>
              <Grid item sx={{ display: 'flex', justifyContent: 'end' }} xs={6}>
                <FormControl>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isSelectAllUnsharedNamespaces}
                        onChange={handleSelectAllUnsharedNamespaces}
                      />
                    }
                    label="Select All"
                  />
                </FormControl>
              </Grid>
            </Grid>
            <Box
              data-testid="unshared-namespace-list"
              sx={{
                maxHeight: NAMESPACE_CHIP_LIST_MAX_HEIGHT,
                overflowY: 'auto',
                padding: 1
              }}
            >
              {filteredUnsharedNamespaces.map((namespace) => {
                const isSelected = selectedNamespacesToAdd.includes(namespace)

                return (
                  <UnsharedNamespaceChip
                    key={namespace}
                    isSelected={isSelected}
                    namespace={namespace}
                    onClick={() =>
                      handleAddSharedNamespaceRequest(namespace, isSelected)
                    }
                  />
                )
              })}
            </Box>
          </Stack>
        )}
        {filteredApprovedNamespaceRequests.length > 0 && (
          <Stack sx={{ rowGap: 1 }}>
            <Typography>Namespaces currently shared with this team</Typography>
            <Box
              data-testid="shared-namespace-list"
              sx={{
                maxHeight: NAMESPACE_CHIP_LIST_MAX_HEIGHT,
                overflowY: 'auto',
                padding: 1
              }}
            >
              {filteredApprovedNamespaceRequests.map(
                (approvedNamespaceRequest) => {
                  const { namespace } = approvedNamespaceRequest

                  return (
                    <SharedNamespaceChip
                      key={namespace}
                      namespace={namespace}
                      onDelete={() =>
                        handleRevokeSharedNamespaceAccess(
                          approvedNamespaceRequest
                        )
                      }
                    />
                  )
                }
              )}
            </Box>
          </Stack>
        )}
      </Stack>
      <Stack direction="row" sx={{ columnGap: 1 }}>
        <LoadingButton
          color="primary"
          disabled={isSaveDisabled}
          loading={isSubmitting}
          variant="contained"
          onClick={handleSubmit}
        >
          {getSubmitButtonText(
            filteredPendingNamespaceRequests,
            approvedNamespaceRequestsToRemove,
            selectedNamespacesToAdd
          )}
        </LoadingButton>
        <Button
          color="secondary"
          variant="contained"
          onClick={handleCloseDrawer}
        >
          Cancel
        </Button>
        {filteredApprovedNamespaceRequests.length > 0 && (
          <Button
            color="error"
            variant="contained"
            onClick={handleUnshareAllNamespaces}
          >
            Unshare All
          </Button>
        )}
      </Stack>
    </Stack>
  )
}
